import { Delegate } from './delegate.js';

/**
 * PDP "Buy more, save more" quantity tiers.
 *
 * Tiers come from the Discounty app embed (window.$Discounty), which renders the campaign
 * that actually applies at checkout into the page. Reading them here keeps the displayed
 * savings in sync with what the customer is really charged — selecting a tier only sets the
 * quantity, Discounty applies the discount itself.
 *
 * The quantity input is the single source of truth: however it changes (tier click, +/−,
 * typing, sticky bar), the matching tier is highlighted and every ATC shows the total.
 *
 * Discounty's shape (undocumented, minified SDK — guarded accordingly):
 *   products[].variants[].discounts[] = { discount_type, operand_type, tiers: [{criteria, discount}] }
 *   discount_type 1 = PERCENTAGE; operand_type 1 = COUNT_OF_ITEMS
 */
const PERCENTAGE = 1;
const COUNT_OF_ITEMS = 1;
const READY_POLL_MS = 100;
const READY_TIMEOUT_MS = 5000;

class QuantityDiscount extends HTMLElement {
  #delegate = null;
  #readyPoll = 0;
  #syncFrame = 0;
  #unsubscribeVariantChange = null;
  #unsubscribeQuantityRules = null;
  #discounty = null;
  #variant = null;
  #tiers = [];

  async connectedCallback() {
    /* Section re-renders move this node, which re-runs connectedCallback -- bail out so a
       second poll and a second set of listeners can't stack up. */
    if (this.#delegate) return;

    const { sectionId } = this.dataset;
    this.tierList = this.querySelector('.product__quantity-discount-tiers');
    this.quantityInput = document.getElementById(`Quantity-${sectionId}`);
    this.#variant = this.getInitialVariant();
    this.availableQuantity = this.parseAvailableQuantity(this.dataset.availableQuantity);

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="select-quantity-tier"]', this.onTierClick);
    /* Main and sticky inputs share this section id and mirror each other, so one listener
       on the document covers +/−, typing and tier clicks alike. */
    document.addEventListener('change', this.onQuantityChange);
    this.#unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.onVariantChange
    );
    /* product-info re-fetches the section after every cart update, so the stock left for
       this variant (inventory minus cart) arrives here too. */
    this.#unsubscribeQuantityRules = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules,
      this.onQuantityRules
    );

    this.#discounty = await this.whenDiscountyReady();
    /* A variant swap disconnects this element, and the poll can outlive that by seconds --
       rendering into a detached node would duplicate the tiers if it is re-attached. */
    if (!this.isConnected) return;
    this.render();
  }

  disconnectedCallback() {
    this.#delegate?.destroy();
    this.#delegate = null;
    clearInterval(this.#readyPoll);
    cancelAnimationFrame(this.#syncFrame);
    document.removeEventListener('change', this.onQuantityChange);
    this.#unsubscribeVariantChange?.();
    this.#unsubscribeVariantChange = null;
    this.#unsubscribeQuantityRules?.();
    this.#unsubscribeQuantityRules = null;
  }

  /**
   * The app embed defines $Discounty in an inline script near the end of <body>, so it may
   * not exist yet when this module runs.
   */
  whenDiscountyReady() {
    return new Promise((resolve) => {
      if (window.$Discounty) return resolve(window.$Discounty);

      const startedAt = Date.now();
      this.#readyPoll = setInterval(() => {
        if (window.$Discounty || Date.now() - startedAt > READY_TIMEOUT_MS) {
          clearInterval(this.#readyPoll);
          resolve(window.$Discounty || null);
        }
      }, READY_POLL_MS);
    });
  }

  /**
   * The variant rendered with the page. <product-info> may not have upgraded yet, so read
   * its JSON directly rather than relying on its currentVariant.
   */
  getInitialVariant() {
    const json = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (json) {
      try {
        return JSON.parse(json);
      } catch (error) {
        /* Fall through to the id-only variant below. */
      }
    }

    const variantId = Number(this.quantityInput?.dataset.quantityVariantId);
    return variantId ? { id: variantId } : null;
  }

  /**
   * The current variant as Discounty published it. No fallback to another variant: a
   * variant outside the campaign must show no tiers, not a sibling's.
   */
  getDiscountyVariant() {
    const variantId = Number(this.#variant?.id);
    if (!variantId) return null;
    const variants = (this.#discounty?.products || []).flatMap((product) => product.variants || []);
    return variants.find(({ id }) => Number(id) === variantId) || null;
  }

  /**
   * @returns {{criteria: number, discount: number}[]} quantity tiers, lowest first
   */
  getTiers(variant) {
    const discount = (variant?.discounts || []).find(
      ({ operand_type, discount_type }) => operand_type === COUNT_OF_ITEMS && discount_type === PERCENTAGE
    );

    return (discount?.tiers || [])
      .map(({ criteria, discount: value }) => ({ criteria: Number(criteria), discount: Number(value) }))
      .filter(({ criteria, discount: value }) => criteria > 1 && value > 0)
      .sort((a, b) => a.criteria - b.criteria);
  }

  /**
   * @param {string|undefined} value - blank when stock isn't tracked or overselling is allowed
   * @returns {number}
   */
  parseAvailableQuantity(value) {
    return value === undefined || value === '' ? Infinity : Number(value);
  }

  /**
   * The stock left for the variant, as rendered in a freshly fetched copy of the section.
   * @param {Document} html
   */
  readAvailableQuantity(html) {
    const fresh = html?.querySelector(`quantity-discount[data-section-id="${this.dataset.sectionId}"]`);
    if (fresh) this.availableQuantity = this.parseAvailableQuantity(fresh.dataset.availableQuantity);
  }

  get unitPrice() {
    return Number(this.#variant?.price) || Number(this.getDiscountyVariant()?.price) || 0;
  }

  get quantity() {
    return Math.max(Number(this.quantityInput?.value) || 1, 1);
  }

  /**
   * Discounty tiers are "buy N or more", so the best tier at or below the quantity applies.
   */
  discountFor(quantity) {
    return this.#tiers.reduce((best, { criteria, discount }) => (quantity >= criteria ? discount : best), 0);
  }

  /**
   * Rebuild the tiers for the current variant, or hide the widget when it has none.
   */
  render() {
    const unitPrice = this.unitPrice;
    /* Shopify caps the add at the stock left, so a tier above it would promise a discount the
       customer can't get. */
    const tiers = this.getTiers(this.getDiscountyVariant()).filter(
      ({ criteria }) => criteria <= this.availableQuantity
    );
    /* A lone tier isn't a "buy more, save more" ladder, so it stays hidden too. */
    this.#tiers = tiers.length >= 2 && unitPrice ? tiers : [];

    this.tierList.replaceChildren(
      ...this.#tiers.map(({ criteria, discount }) =>
        this.buildTier({
          quantity: criteria,
          percent: discount,
          unitPrice: Math.max(unitPrice * (1 - discount / 100), 0),
        })
      )
    );

    this.hidden = this.#tiers.length === 0;
    this.sync();
  }

  buildTier({ quantity, percent, unitPrice }) {
    const tier = document.createElement('button');
    tier.type = 'button';
    tier.className = 'product__quantity-discount-tier';
    tier.setAttribute('aria-pressed', 'false');
    tier.dataset.action = 'select-quantity-tier';
    tier.dataset.quantity = quantity;

    const label = this.dataset.tierLabel.replace('[[quantity]]', quantity);
    const save = this.dataset.saveLabel.replace('[[percent]]', percent);
    /* Whole units like the rest of the PDP: an exact 108,30 DKK/stk. overflows narrow cards,
       and the per-unit figure is indicative -- the ATC shows the real total. */
    const price = this.dataset.priceLabel.replace('[[price]]', this.formatMoney(Math.round(unitPrice / 100) * 100));

    tier.append(this.buildSpan('label', label));
    if (save) tier.append(this.buildSpan('badge', save));
    if (price) tier.append(this.buildSpan('price', price));

    return tier;
  }

  buildSpan(modifier, text) {
    const span = document.createElement('span');
    span.className = `product__quantity-discount-tier-${modifier}`;
    span.textContent = text;
    return span;
  }

  formatMoney(cents) {
    return FoxTheme.Currency.formatMoney(Math.round(cents), FoxTheme.settings.moneyFormat);
  }

  get tiers() {
    return Array.from(this.querySelectorAll('.product__quantity-discount-tier'));
  }

  /**
   * Highlight the tier matching the quantity and put its total on every ATC.
   */
  sync() {
    const quantity = this.quantity;
    this.tiers.forEach((tier) =>
      tier.setAttribute('aria-pressed', String(Number(tier.dataset.quantity) === quantity))
    );

    const unitPrice = this.unitPrice;
    if (!unitPrice) return;
    const total = unitPrice * (1 - this.discountFor(quantity) / 100) * quantity;
    this.setSubmitPrices(this.formatMoney(total));
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} tier
   */
  onTierClick = (event, tier) => {
    this.setQuantity(tier.dataset.quantity);
  };

  /**
   * @param {Event} event
   */
  onQuantityChange = (event) => {
    if (!event.target.closest?.(`quantity-input[data-section-id="${this.dataset.sectionId}"]`)) return;
    this.sync();
  };

  /**
   * product-info swaps in the server-rendered unit price, and the sticky bars rewrite theirs
   * from other subscribers of this same event -- so re-apply once they have all run.
   */
  onVariantChange = ({ data: { sectionId, variant, html } }) => {
    if (sectionId !== this.dataset.sectionId) return;

    this.#variant = variant || null;
    this.readAvailableQuantity(html);
    cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => {
      /* Before Discounty is ready, the pending render() picks up the new variant itself. */
      if (this.#discounty) this.render();
      else this.sync();
    });
  };

  onQuantityRules = ({ data: { sectionId, parsedHTML } }) => {
    if (sectionId !== this.dataset.sectionId) return;

    const previous = this.availableQuantity;
    this.readAvailableQuantity(parsedHTML);
    if (this.availableQuantity !== previous && this.#discounty) this.render();
  };

  setQuantity(quantity) {
    if (!this.quantityInput) return;
    this.quantityInput.value = quantity;
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Main ATC plus any sticky bar for this section.
   */
  setSubmitPrices(formattedPrice) {
    const { sectionId } = this.dataset;
    const prices = document.querySelectorAll(
      [
        `#ProductSubmitPrice-${sectionId}`,
        `sticky-atc-bar[data-section="${sectionId}"] .sticky-atc__submit-price`,
        `sticky-atc-bar-minimalist[data-section="${sectionId}"] .sticky-atc__submit-price`,
      ].join(', ')
    );

    /* Leave the struck-through compare-at price alone; only the live amounts change. */
    prices.forEach((price) =>
      price.querySelectorAll('.f-price-item--sale, .f-price-item--regular').forEach((item) => {
        if (!item.querySelector('s')) item.textContent = formattedPrice;
      })
    );
  }
}

if (!customElements.get('quantity-discount')) {
  customElements.define('quantity-discount', QuantityDiscount);
}
